import { useState, useMemo, useEffect } from 'react';
import {
	H3,
	Checkbox,
	Input,
	Modal,
	Select,
	Textarea,
	Flex,
	FlexItem,
	Grid,
	GridItem,
} from '@bigcommerce/big-design';
import { useClient } from '../../../../../context/client';
import { useAsyncEffect } from '../../../../../hooks';
import { createSelectOptions } from '../../../../../utils';
import { useCurrency, useOrderProducts } from '../../../context';
import { updateProducts } from '../../../store/orderProducts';
import { formatVariationOptionsForQuestions } from './Columns';

function QuestionsAndVariationOptions({
	question,
	answers,
	selectedOptionValues,
	handler,
	value,
}) {
	const optionValue = selectedOptionValues?.[question] ?? '';

	return (
		<GridItem>
			<Select
				label={question}
				onOptionChange={(value) => handler(question, value)}
				options={createSelectOptions(answers, false)}
				placeholder="Select Option"
				placement="bottom-start"
				required
				value={optionValue ? optionValue : value}
			/>
		</GridItem>
	);
}

function getProductOptions(product, name, value) {
	return product.productOptions
		.find((option) => option.display_name === name)
		?.option_values.reduce((optionValueObject, optionValue) => {
			if (optionValue.label === value) {
				return {
					...optionValueObject,
					id: optionValue.id,
					label: optionValue.label,
				};
			}
			return optionValueObject;
		}, {});
}

function updateObjectEntry(name, value) {
	return (object) => ({
		...object,
		[name]: value,
	});
}

function stateUpdateHandler(stateUpdater, handler) {
	return (...args) => stateUpdater(handler(...args));
}

function commonQuestionFormatMapper(selectedAnswer, handler) {
	return (question) => ({
		question: question.question,
		answers: question.answers,
		selectedAnswer,
		handler,
	});
}

function getSelectedVariant(product) {
	return (name, value) =>
		product.selectedVariant.option_values.map((variant) => {
			if (variant?.option_display_name !== name) return variant;
			const foundProductOption = getProductOptions(product, name, value);
			return {
				...variant,
				id: foundProductOption.id,
				label: foundProductOption.label,
			};
		});
}

function handleStateUpdates(...handlers) {
	return (name, value) => {
		for (const handler of handlers) handler(name, value);
	};
}

function findQuestionValue(product, question) {
	return product.option_values[question];
}

function useConfigurableOptions(product) {
	const [selectedOptionValues, setSelectedOptionValues] = useState({});
	const [selectedVariation, setSelectedVariation] = useState([]);

	const configurableOptions = useMemo(() => {
		const formattedQuestions = product.questions.map(
			commonQuestionFormatMapper(
				selectedOptionValues,
				handleStateUpdates(
					stateUpdateHandler(
						setSelectedOptionValues,
						updateObjectEntry,
					),
				),
			),
		);

		return formatVariationOptionsForQuestions(
			product,
			handleStateUpdates(
				stateUpdateHandler(setSelectedOptionValues, updateObjectEntry),
				stateUpdateHandler(
					setSelectedVariation,
					getSelectedVariant(product),
				),
			),
			formattedQuestions,
		);
	}, [product, selectedOptionValues, selectedVariation]);

	return [configurableOptions, selectedOptionValues, selectedVariation];
}

function getOptionValues(selectedConfigurableOptions, optionValues) {
	const selectedOptionValuesEntries = Object.entries(
		selectedConfigurableOptions,
	);
	// if the selectedOptionValuesEntries has length then there was a previous selection
	// spread the optionValues first so all the questions that didn't change get added
	// then spread the selectedConfiguableOptions to add the questions with answers that changed.
	return selectedOptionValuesEntries.length
		? {
				...optionValues,
				...selectedConfigurableOptions,
		  }
		: { ...optionValues };
}

export default function AdditionalInfoPopup(props) {
	const { isOpen, setIsOpen, product } = props;
	const client = useClient();
	const [currency] = useCurrency();
	const [, dispatchOrderProducts] = useOrderProducts();
	const [addOnOptions, setAddOnOptions] = useState({});
	const [selectedAddOns, setSelectedAddOns] = useState({});
	const currencyFormatter = new Intl.NumberFormat('en-US', {
		style: 'currency',
		currency,
		minimumFractionDigits: 2,
	});

	const [configurableOptions, selectedOptionValues, selectedVariation] =
		useConfigurableOptions(product);

	// get the previous selected answers off the product.option_values and set the configurableOptions selected Answer to the previous answer
	// previousOptionSelection is a variable created to find if the product.option_values object has key value pairs to test length
	const previousOptionSelection = Object.entries(product.option_values);
	// this configurableOptionsMap will add a previously selected answer to the corresponding key
	//  and the modal will show the previous selected answers upon reopening it.
	const configurableOptionsMap = configurableOptions.map((option) => ({
		...option,
		selectedAnswer: previousOptionSelection.length
			? findQuestionValue(product, option.question)
			: '',
	}));

	function updateSelectedAddOns(id) {
		setSelectedAddOns((previousSelectedAddons) => ({
			...previousSelectedAddons,
			[id]: {
				...previousSelectedAddons[id],
				added: !previousSelectedAddons[id].added,
				quantity: 1,
			},
		}));
	}

	function updateSelectedAddOnsQty(event, id) {
		let quantity = event.target.value;
		setSelectedAddOns((previousSelectedAddons) => ({
			...previousSelectedAddons,
			[id]: {
				...previousSelectedAddons[id],
				added: previousSelectedAddons[id].added,
				quantity: quantity,
			},
		}));
	}

	function isCheckboxSelected(id) {
		if (selectedAddOns[id] !== undefined) return selectedAddOns[id].added;
		return false;
	}

	function updateProduct(name, value) {
		dispatchOrderProducts(updateProducts({ ...product, [name]: value }));
	}

	async function getAndSetAllAddonOptions(product) {
		const allAddOnOptions = await client.getProductsBySku(
			product.addons.reduce((addons, addon) => {
				const [sku, productId] = addon.split(':');
				if (!productId || Number(productId) === product.product_id)
					return [...addons, sku];
				return addons;
			}, []),
		);
		// Initialize selected status of add ons as false.
		const selectableAddOns = Object.fromEntries(
			allAddOnOptions.map((addOn) => [
				addOn.id,
				{ ...addOn, added: false },
			]),
		);

		setAddOnOptions(allAddOnOptions);
		setSelectedAddOns(selectableAddOns);
	}

	useAsyncEffect(
		async (factory, deps) => {
			if (product.addons?.length > 0)
				await getAndSetAllAddonOptions(product);
		},
		[product],
	);

	return (
		<Modal
			actions={[
				{
					text: 'Cancel',
					variant: 'subtle',
					onClick: () => {
						setIsOpen(false);
					},
				},
				{
					text: 'Done',
					onClick: () => {
						dispatchOrderProducts(
							updateProducts({
								...product,
								selectedAddOns: Object.values(selectedAddOns),
								option_values: getOptionValues(
									selectedOptionValues,
									product.option_values,
								),
								// updated the selectedVariant with the value selected for the product option variation
								selectedVariant: {
									...product.selectedVariant,
									option_values: selectedVariation,
								},
							}),
						);
						setIsOpen(false);
					},
				},
			]}
			header={product.product_name}
			isOpen={isOpen}
			onClose={() => setIsOpen(false)}
			closeOnEscKey={true}
			closeOnClickOutside={false}
		>
			{/* This element is just to take focus when the modal opens, so that the dropdowns do not open automatically.*/}
			<div tabIndex="0">
				{configurableOptionsMap.length > 0 && (
					<Grid gridColumns="repeat(2, 1fr)" padding="medium">
						{/* Options in the product, are the names which are mapped to the options which are retrieved with the "product" request. Compile all option selects if they exist. */}
						{configurableOptionsMap.map(
							({
								question,
								answers,
								handler,
								selectedAnswer,
							}) => (
								<QuestionsAndVariationOptions
									key={question}
									question={question}
									answers={answers}
									selectedOptionValues={selectedOptionValues}
									handler={handler}
									value={selectedAnswer}
								/>
							),
						)}
					</Grid>
				)}
				{addOnOptions.length > 0 && (
					<Grid gridColumns="repeat(1, 1fr)" padding="medium">
						<H3>Add Ons</H3>
						{addOnOptions.map(({ id, name, price, sale_price }) => (
							<div key={id}>
								<Flex flexWrap="wrap">
									<FlexItem flexBasis="100%">
										<Checkbox
											label={`${name} + ${currencyFormatter.format(
												sale_price || price,
											)}`}
											checked={selectedAddOns[id]?.added}
											onChange={() =>
												updateSelectedAddOns(id)
											}
										/>
									</FlexItem>
									<FlexItem flexBasis="50%">
										<Input
											label="Quantity"
											type="number"
											error={isNaN(
												selectedAddOns[id]?.quantity,
											)}
											required
											onChange={(event) =>
												updateSelectedAddOnsQty(
													event,
													id,
												)
											}
											value={
												selectedAddOns[id]?.quantity ??
												1
											}
											disabled={!isCheckboxSelected(id)}
										/>
									</FlexItem>
								</Flex>
							</div>
						))}
					</Grid>
				)}
				<Flex padding="medium">
					<FlexItem flexGrow={0} flexOrder={0} flexBasis="70%">
						<Textarea
							label="Row Comments"
							placeholder="Enter row comments"
							rows={3}
							resize={true}
							value={product.row_comment ?? ''}
							onChange={({ currentTarget }) =>
								updateProduct(
									'row_comment',
									currentTarget.value,
								)
							}
							autoFocus={true}
						/>
					</FlexItem>
				</Flex>
			</div>
		</Modal>
	);
}
